/**
 * Specification Key Constants
 *
 * Centralized constants for resource specification keys used across frontend and backend.
 * This ensures consistency and prevents hardcoded strings scattered across the codebase.
 *
 * @see Research Report Section 8, Phase 1
 */

import { fromMinorUnits, type CurrencyCodeMetadata } from "@/lib/currency";

// =============================================================================
// Frontend Specification Keys (UI Layer)
// =============================================================================
/**
 * Keys used in the frontend UI components and stores.
 * These represent the user-facing specification model.
 */
export const FRONTEND_SPEC_KEYS = {
  CPU: "cpu",
  MEMORY: "memory",
  DISK: "disk",
  BANDWIDTH: "bandwidth",
} as const;

export type FrontendSpecKey =
  (typeof FRONTEND_SPEC_KEYS)[keyof typeof FRONTEND_SPEC_KEYS];

// =============================================================================
// Backend Specification Keys (API Layer)
// =============================================================================
/**
 * Keys used in backend API requests for resource specifications.
 * These match the backend ResourceSpecificationDto contract.
 */
export const BACKEND_SPEC_KEYS = {
  CORES: "cores",
  MEMORY: "memory",
  DISK: "disk",
  DISK_SIZE_GB: "diskSizeGB",
  BANDWIDTH: "bandwidth",
  TEMPLATE_ID: "templateId",
  TEMPLATE_NAME: "templateName",
  PRODUCT_ID: "productId",
  REGION: "region",
  CHIP_TYPE: "chipType",
} as const;

export type BackendSpecKey =
  (typeof BACKEND_SPEC_KEYS)[keyof typeof BACKEND_SPEC_KEYS];

// =============================================================================
// Pricing Component Slugs (Pricing API Layer)
// =============================================================================
/**
 * Slugs used in pricing calculation API requests.
 * These match the backend PricingComponentSlug enum.
 */
export const PRICING_COMPONENT_SLUGS = {
  CPU_CORE: "cpu-core",
  MEMORY_GB: "memory-gb",
  SSD_STORAGE_GB: "ssd-storage-gb",
  BANDWIDTH_GB: "bandwidth-gb",
  PUBLIC_IP: "public-ip",
  NUM_BACKUPS: "num-backups",
  NUM_SNAPSHOTS: "num-snapshots",
  IP_TIER: "ip-tier",
  BACKUP_STORAGE_GB: "backup-storage-gb",
  SNAPSHOT_STORAGE_GB: "snapshot-storage-gb",
  OBJECT_STORAGE_GB: "object-storage-gb",
} as const;

export type PricingComponentSlug =
  (typeof PRICING_COMPONENT_SLUGS)[keyof typeof PRICING_COMPONENT_SLUGS];

// =============================================================================
// Pricing Component Display Names
// =============================================================================
/**
 * Human-readable names for pricing components.
 * Used in UI displays and breakdown parsing.
 */
export const PRICING_COMPONENT_NAMES = {
  CPU_CORE: "CPU Core",
  MEMORY: "Memory",
  SSD_STORAGE: "SSD Storage",
  BANDWIDTH: "Bandwidth",
} as const;

// =============================================================================
// Specification Defaults
// =============================================================================
/**
 * Default values for resource specifications.
 * Used as fallbacks when values are missing or invalid.
 */
export const SPEC_DEFAULTS = {
  CPU: 2,
  MEMORY_GB: 4,
  DISK_GB: 50,
  BANDWIDTH_GB: 1000,
} as const;

export const MAX_BACKUP_SNAPSHOT_COUNT = 7;

// =============================================================================
// Transform Functions
// =============================================================================

/**
 * Transform frontend ResourceSpecs to backend API specification format.
 * Handles key mapping and unit conversions.
 *
 * @param specs - Frontend ResourceSpecs object
 * @returns Backend specification object for API requests
 *
 * @example
 * const frontendSpecs = { cores: 4, memory: 8, disk: 100, bandwidth: 2000 };
 * const backendSpecs = transformToBackendSpecs(frontendSpecs);
 * // Result: { cores: 4, memory: 8192, disk: 100 }
 */
export function transformToBackendSpecs(specs: {
  cores?: number;
  memory?: number;
  disk?: number;
  bandwidth?: number;
}): {
  cores: number;
  memory: number; // In MB
  disk: number; // In GB
} {
  return {
    [BACKEND_SPEC_KEYS.CORES]: specs.cores ?? SPEC_DEFAULTS.CPU,
    [BACKEND_SPEC_KEYS.MEMORY]:
      (specs.memory ?? SPEC_DEFAULTS.MEMORY_GB) * 1024, // GB to MB conversion
    [BACKEND_SPEC_KEYS.DISK]: specs.disk ?? SPEC_DEFAULTS.DISK_GB,
  };
}

/**
 * Transform frontend ResourceSpecs to pricing API configuration format.
 * Uses pricing component slugs instead of direct specification keys.
 *
 * @param specs - Frontend ResourceSpecs object
 * @returns Pricing configuration object for pricing API requests
 *
 * @example
 * const frontendSpecs = { cores: 4, memory: 8, disk: 100, bandwidth: 2000 };
 * const pricingConfig = transformToPricingConfig(frontendSpecs);
 * // Result: { 'cpu-core': 4, 'memory-gb': 8, 'ssd-storage-gb': 100, 'bandwidth-gb': 2000 }
 */
export function transformToPricingConfig(specs: {
  cores?: number;
  memory?: number;
  disk?: number;
  bandwidth?: number;
  backupCount?: number;
  snapshotCount?: number;
  additionalIps?: number;
}): Record<string, number> {
  return {
    [PRICING_COMPONENT_SLUGS.CPU_CORE]: specs.cores ?? SPEC_DEFAULTS.CPU,
    [PRICING_COMPONENT_SLUGS.MEMORY_GB]:
      specs.memory ?? SPEC_DEFAULTS.MEMORY_GB,
    [PRICING_COMPONENT_SLUGS.SSD_STORAGE_GB]:
      specs.disk ?? SPEC_DEFAULTS.DISK_GB,
    [PRICING_COMPONENT_SLUGS.NUM_BACKUPS]: specs.backupCount ?? 0,
    [PRICING_COMPONENT_SLUGS.NUM_SNAPSHOTS]: specs.snapshotCount ?? 0,
    [PRICING_COMPONENT_SLUGS.PUBLIC_IP]: specs.additionalIps ?? 1,
  };
}

/**
 * Parse pricing component breakdown from backend response.
 * Maps pricing component names to their costs.
 *
 * @param breakdown - Component breakdown object from pricing API
 * @param currency - Currency code for proper conversion from minor units (default: USD)
 * @returns Parsed breakdown with standardized keys
 *
 * @example
 * const apiBreakdown = { 'CPU Core': 1500, 'Memory': 800, 'SSD Storage': 500 };
 * const parsed = parsePricingBreakdown(apiBreakdown, 'USD');
 * // Result: { cores: 15.00, memory: 8.00, disk: 5.00 } (for USD, dividing by 100)
 *
 * @example
 * const apiBreakdown = { 'CPU Core': 840, 'RAM': 1360, 'SSD Storage': 600 };
 * const parsed = parsePricingBreakdown(apiBreakdown, 'VND');
 * // Result: { cores: 840, memory: 1360, disk: 600 } (for VND, no conversion)
 */
export function parsePricingBreakdown(
  breakdown: Record<string, number>,
  currency: string = "USD",
): {
  cores: number;
  memory: number;
  disk: number;
  bandwidth?: number;
  ipTier?: number;
  backup?: number;
  snapshot?: number;
} {
  const currencyCode = (currency || "USD") as CurrencyCodeMetadata;

  const convertFromMinorUnits = (amount?: number) =>
    typeof amount === "number" ? fromMinorUnits(amount, currencyCode) : 0;

  return {
    cores: convertFromMinorUnits(breakdown[PRICING_COMPONENT_SLUGS.CPU_CORE]),
    memory: convertFromMinorUnits(
      breakdown[PRICING_COMPONENT_SLUGS.MEMORY_GB] ?? breakdown["RAM"],
    ),
    disk: convertFromMinorUnits(
      breakdown[PRICING_COMPONENT_SLUGS.SSD_STORAGE_GB],
    ),
    bandwidth: breakdown[PRICING_COMPONENT_SLUGS.BANDWIDTH_GB]
      ? convertFromMinorUnits(breakdown[PRICING_COMPONENT_SLUGS.BANDWIDTH_GB])
      : undefined,
    ipTier: convertFromMinorUnits(
      breakdown[PRICING_COMPONENT_SLUGS.IP_TIER] ?? breakdown["Public IP"],
    ),
    backup: convertFromMinorUnits(
      breakdown[PRICING_COMPONENT_SLUGS.BACKUP_STORAGE_GB] ??
        breakdown["Backup"],
    ),
    snapshot: convertFromMinorUnits(
      breakdown[PRICING_COMPONENT_SLUGS.SNAPSHOT_STORAGE_GB] ??
        breakdown["Snapshot"],
    ),
  };
}
